import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { routeName, sendPageView } from "@/lib/events";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/lib/content";
import ChangeTempPassword from "@/pages/ChangeTempPassword";
import ContentEditor from "@/pages/admin/ContentEditor";
import { A11yProvider } from "@/context/A11yContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { RequireAuth } from "@/components/layout/RequireAuth";
import Home from "@/pages/Home";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmail from "@/pages/ConfirmEmail";
import Profile from "@/pages/Profile";
import AssessmentWizard from "@/pages/AssessmentWizard";
import SubmissionDetail from "@/pages/SubmissionDetail";
import { AdminLayout, Perm, AdminHome } from "@/pages/admin/AdminLayout";
import { UtentiPermessi, Amministratori } from "@/pages/admin/UsersPage";
import { UserDetail } from "@/pages/admin/UserDetail";
import { MioAccount } from "@/pages/admin/AccountPage";
import { Panoramica, Analisi, Territori } from "@/pages/admin/DashboardPages";
import { Aziende, Esporta } from "@/pages/admin/CompaniesPages";
import AziendaDetail from "@/pages/admin/AziendaDetail";
import { Sedi, Impostazioni } from "@/pages/admin/ManagePages";
import { QuestionarioVersions, QuestionarioEditor } from "@/pages/admin/QuestionnairePages";
import { Campi } from "@/pages/admin/FieldsPage";
import { PrivacyInfo, AccessibilityStatement, NotFound } from "@/pages/StaticPages";

const RouteTracker = () => {
    const { pathname, search } = useLocation();
    const name = routeName(pathname, search);
    useEffect(() => { sendPageView(name); }, [name]);
    return null;
};

function App() {
    return (
        <A11yProvider>
            <BrowserRouter>
                <AuthProvider>
                    <ContentProvider>
                    <RouteTracker />
                    <Routes>
                        <Route element={<AppLayout />}>
                            <Route index element={<Home />} />
                            <Route path="registrazione" element={<Register />} />
                            <Route path="accedi" element={<Login />} />
                            <Route path="password-dimenticata" element={<ForgotPassword />} />
                            <Route path="reimposta-password" element={<ResetPassword />} />
                            <Route path="conferma-email" element={<ConfirmEmail />} />
                            <Route path="profilo" element={<RequireAuth roles={["azienda"]}><Profile /></RequireAuth>} />
                            <Route path="assessment" element={<RequireAuth roles={["azienda"]}><AssessmentWizard /></RequireAuth>} />
                            <Route path="assessment/risultati/:id" element={<RequireAuth roles={["azienda"]}><SubmissionDetail /></RequireAuth>} />
                            <Route path="imposta-password" element={<ResetPassword invite />} />
                            <Route path="scegli-password" element={<RequireAuth><ChangeTempPassword /></RequireAuth>} />
                            <Route path="amministrazione" element={<RequireAuth roles={["admin_sede", "super_admin", "system_admin"]}><AdminLayout /></RequireAuth>}>
                                <Route index element={<AdminHome><Panoramica /></AdminHome>} />
                                <Route path="aziende" element={<Perm k="aziende"><Aziende /></Perm>} />
                                <Route path="aziende/:id" element={<Perm k="aziende"><AziendaDetail /></Perm>} />
                                <Route path="esporta" element={<Perm k="aziende"><Esporta /></Perm>} />
                                <Route path="analytics" element={<Perm k="analytics"><AnalyticsPage /></Perm>} />
                                <Route path="registro" element={<Perm k="registro"><AnalyticsPage key="registro" view="registro" /></Perm>} />
                                <Route path="contenuti" element={<Perm k="contenuti"><ContentEditor /></Perm>} />
                                <Route path="territori" element={<Perm k="territori"><Territori /></Perm>} />
                                <Route path="analisi" element={<Perm k="analisi"><Analisi /></Perm>} />
                                <Route path="sedi" element={<Perm k="sedi"><Sedi /></Perm>} />
                                <Route path="amministratori" element={<Perm k="amministratori"><Amministratori /></Perm>} />
                                <Route path="questionario" element={<Perm k="questionario"><QuestionarioVersions /></Perm>} />
                                <Route path="questionario/bozza" element={<Perm k="questionario" level="M"><QuestionarioEditor /></Perm>} />
                                <Route path="questionario/anteprima/:version" element={<Perm k="questionario"><AssessmentWizard preview /></Perm>} />
                                <Route path="campi" element={<Perm k="campi"><Campi /></Perm>} />
                                <Route path="impostazioni" element={<Perm system><Impostazioni /></Perm>} />
                                <Route path="utenti" element={<Perm system><UtentiPermessi /></Perm>} />
                                <Route path="utenti/:id" element={<Perm system><UserDetail system /></Perm>} />
                                <Route path="amministratori/:id" element={<Perm k="amministratori"><UserDetail /></Perm>} />
                                <Route path="account" element={<MioAccount />} />
                            </Route>
                            <Route path="informativa-privacy" element={<PrivacyInfo />} />
                            <Route path="accessibilita" element={<AccessibilityStatement />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Routes>
                    </ContentProvider>
                </AuthProvider>
            </BrowserRouter>
        </A11yProvider>
    );
}

export default App;
